// src/utils/routePrintHtml.js — construcción de las hojas imprimibles del
// Planificador (por técnico y resumen operativo). Funciones puras (sin
// React/DOM), separadas de RouteGenerator.js para poder probarlas de forma
// aislada: ese componente importa react-leaflet (ESM sin transformar), así
// que Jest no puede cargarlo para testear nada que viva ahí dentro.
//
// Rediseño visual 2026-10-06 (pedido del usuario): cada técnico con su
// propia hoja con gráficos (tira de KPI, medidor de capacidad, línea de
// tiempo del día) para iniciar la ruta con la información bien estructurada,
// y lo mismo para el director de operaciones (KPIs, gráfico de barras de
// carga por técnico, donut de nuevas/ya asignadas).
import { esUrgente } from './urgencia';

// Mismos colores que el resto de la app (Planificador en pantalla) — color
// por identidad de técnico, nunca por posición/ranking.
export const TECH_MAP_COLORS = {
  Benito: '#3b82f6',
  Pablo:  '#22c55e',
  Dani:   '#f59e0b',
  Ivan:   '#ef4444',
};

export function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

// Bloque de notas IA bajo la fila de la orden (Parte 1 del plan de notas IA,
// 2026-10-05) — si una orden no tiene nota (la IA falló para ella, o no
// tenía clienteId) simplemente no sale nada, no rompe la fila.
export function buildNotaIAHtml(nota) {
  if (!nota) return '';
  const partes = [];
  // diagnostico (reescritura del prompt 2026-10-07): sustituye a los
  // antiguos causaProbable/solucionPropuesta sueltos por una lista de hasta
  // 3 puntos (causas con comprobación rápida, o puntos de mantenimiento a
  // revisar) — la `fuente` de cada uno (documentacion/historial/
  // casos_similares/deduccion) se guarda en el dato para poder auditarla,
  // pero no se imprime: añadiría ruido a una hoja pensada para un vistazo
  // rápido antes de la visita.
  if (nota.diagnostico?.length) {
    const puntos = nota.diagnostico.map(d => `<div class="ia-diag">${escapeHtml(d.texto)}</div>`).join('');
    partes.push(`<div class="ia-causa"><strong>IA ·</strong>${puntos}</div>`);
  }
  if (nota.riesgos?.length) {
    partes.push(`<div class="ia-riesgos">${nota.riesgos.map(r => `<span class="riesgo">⚠ ${escapeHtml(r)}</span>`).join(' ')}</div>`);
  }
  if (nota.materialSugerido?.length) {
    const items = nota.materialSugerido.map(m => {
      // Dos ejes independientes: ¿está documentada en el despiece del
      // equipo? (enDespiece, decide la IA, con su código como prueba) y
      // ¿la tenemos en stock nosotros? (disponible, se valida en código
      // contra el catálogo real). prioridad (llevar/por_si_acaso) es un
      // tercer eje: ¿hace falta meterla en la furgoneta a la primera?
      const stockEtiqueta = m.disponible ? '✓ en stock' : m.encontradoEnCatalogo ? 'sin stock' : 'no en catálogo';
      const despieceEtiqueta = m.codigoDespiece ? ` · despiece: ${escapeHtml(m.codigoDespiece)}` : ' · sin despiece';
      const esSecundaria = m.prioridad === 'por_si_acaso';
      const prioridadEtiqueta = esSecundaria ? ' · por si acaso' : '';
      return `<span class="material ${m.disponible ? 'mat-ok' : 'mat-warn'}${esSecundaria ? ' mat-secundario' : ''}">${escapeHtml(m.nombre)} (${stockEtiqueta}${despieceEtiqueta}${prioridadEtiqueta})</span>`;
    }).join(' ');
    partes.push(`<div class="ia-material">🔧 ${items}</div>`);
  }
  if (nota.oportunidadComercial) partes.push(`<div class="ia-oportunidad">💡 ${escapeHtml(nota.oportunidadComercial)}</div>`);
  if (!partes.length) return '';
  return `<div class="ia-notas">${partes.join('')}</div>`;
}

// ── Ventana de trabajo y helpers visuales compartidos (hoja impresa) ─────────
// Mismos límites que la regla de negocio del generador (09:45-17:30) — ver
// recortarPorVentana() en generate-routes.js.
const VENTANA_INICIO_MIN = 9 * 60 + 45;
const VENTANA_FIN_MIN    = 17 * 60 + 30;
const VENTANA_TOTAL_MIN  = VENTANA_FIN_MIN - VENTANA_INICIO_MIN;

function minFromHHMM(hhmm) {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
}

// Tira de tarjetas KPI (número grande + etiqueta) — mismo patrón visual que
// las StatCard del Dashboard, adaptado a HTML estático para impresión.
export function kpiStripHtml(tiles) {
  return `<div class="kpi-strip">${tiles.map(t => `
    <div class="kpi-tile">
      <p class="kpi-val"${t.color ? ` style="color:${t.color}"` : ''}>${t.val}</p>
      <p class="kpi-label">${t.label}</p>
    </div>`).join('')}</div>`;
}

// Medidor de capacidad: 5 segmentos (tope de la regla de negocio), rellenos
// con el color del técnico hasta el nº de paradas reales.
export function gaugeHtml(n, max, color) {
  const segs = Array.from({ length: max }, (_, i) =>
    `<div class="gauge-seg${i < n ? ' filled' : ''}"${i < n ? ` style="background:${color}"` : ''}></div>`
  ).join('');
  return `<div class="gauge"><div class="gauge-segs">${segs}</div><span class="gauge-label">${n}/${max} paradas</span></div>`;
}

// Línea de tiempo visual del día (09:45→17:30): cada parada es un bloque de
// ancho proporcional a su duración real, posicionado en su horario real —
// de un vistazo se ve cómo queda repartido el día, no solo una lista.
export function timelineHtml(orders) {
  if (!orders.length) return '';
  const bloques = orders.map(o => {
    const ini = minFromHHMM(o.horaInicio), fin = minFromHHMM(o.horaFin);
    if (ini == null || fin == null) return '';
    const left  = Math.max(0, Math.min(100, (ini - VENTANA_INICIO_MIN) / VENTANA_TOTAL_MIN * 100));
    const width = Math.max(1.5, (fin - ini) / VENTANA_TOTAL_MIN * 100);
    const cls = o.yaAsignada ? 'tl-exist' : (o.prioridad === 2 ? 'tl-p2' : 'tl-p1');
    return `<div class="tl-block ${cls}" style="left:${left}%;width:${width}%" title="${escapeHtml(o.customerName)} · ${o.horaInicio}–${o.horaFin}">${o.orden}</div>`;
  }).join('');

  // Comida: primer hueco >=30min entre paradas que empiece en [13:30,15:00) —
  // mismo criterio de detección que evaluarRegla1 (_diagnosticoReglas.js).
  let comidaHtml = '';
  for (let i = 0; i < orders.length - 1; i++) {
    const finA = minFromHHMM(orders[i].horaFin), iniB = minFromHHMM(orders[i + 1].horaInicio);
    if (finA == null || iniB == null) continue;
    if (finA >= 13 * 60 + 30 && finA < 15 * 60 && (iniB - finA) >= 30) {
      const left  = (finA - VENTANA_INICIO_MIN) / VENTANA_TOTAL_MIN * 100;
      const width = (iniB - finA) / VENTANA_TOTAL_MIN * 100;
      comidaHtml = `<div class="tl-block tl-comida" style="left:${left}%;width:${width}%" title="Comida">🍽</div>`;
      break;
    }
  }

  return `<div class="timeline">
    <div class="tl-track">${bloques}${comidaHtml}</div>
    <div class="tl-axis"><span>09:45</span><span>13:30</span><span>17:30</span></div>
  </div>`;
}

// Enlace de Google Maps con toda la ruta del técnico en el orden ya
// calculado (oficina → paradas → última parada), para el QR de la hoja
// impresa (pedido del usuario 2026-10-08) — el técnico lo escanea y entra
// directo en navegación turn-by-turn, sin teclear ninguna dirección. Google
// Maps soporta de sobra las ≤5-7 paradas/día de aquí en un solo enlace
// (api=1, formato documentado de "Get a Google Maps URL").
export function buildGoogleMapsUrl(orders, officeLat, officeLng) {
  const conCoords = (orders || []).filter(o => o.lat != null && o.lng != null);
  if (!conCoords.length) return null;
  const destino = conCoords[conCoords.length - 1];
  const waypoints = conCoords.slice(0, -1);
  const params = new URLSearchParams({
    api: '1',
    origin: `${officeLat},${officeLng}`,
    destination: `${destino.lat},${destino.lng}`,
    travelmode: 'driving',
  });
  if (waypoints.length) params.set('waypoints', waypoints.map(o => `${o.lat},${o.lng}`).join('|'));
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

// qrPorTecnico: { [tecnico]: { dataUrl, url } } — generados aparte (RouteGenerator.js,
// vía la librería `qrcode` ya usada en PegatinasView.js) porque esta función
// es síncrona y QRCode.toDataURL() es async; aquí solo se incrusta el resultado.
export function buildPrintHTML(result, tecnicoFilter = null, notasPorOrden = {}, qrPorTecnico = {}) {
  const { fecha } = result;
  const routes = tecnicoFilter
    ? Object.fromEntries(Object.entries(result.routes).filter(([k]) => k === tecnicoFilter))
    : result.routes;
  const dateLabel = new Date(fecha + 'T12:00:00').toLocaleDateString('es-ES', {
    weekday:'long', day:'2-digit', month:'long', year:'numeric',
  });
  const techSections = Object.values(routes).map(rt => {
    const color = TECH_MAP_COLORS[rt.tecnico] || '#6b7280';
    const cards = rt.orders.map(o => {
      const rowClass = o.yaAsignada ? 'exist' : (o.prioridad===2?'p2':'p1');
      const comentario = o.privateComments ? `<div class="comment">“${escapeHtml(o.privateComments)}”</div>` : '';
      const notaHtml = buildNotaIAHtml(notasPorOrden[o.id]);
      const esperaHtml = o.diasEspera!==null && !o.yaAsignada
        ? `<span class="chip ${esUrgente(o.diasEspera)?'chip-alert':'chip-gray'}">${o.diasEspera}d espera</span>` : '';
      return `<div class="stop ${rowClass}">
        <div class="stop-num" style="background:${color}">${o.orden}</div>
        <div class="stop-body">
          <div class="stop-top">
            <span class="mono stop-time">${o.horaInicio}–${o.horaFin}</span>
            <span class="tag ${o.yaAsignada?'tag-exist':''}">${o.tipoLabel}</span>
            ${esperaHtml}
          </div>
          <div class="cname">${escapeHtml(o.customerName)}</div>
          <div class="ref">${escapeHtml(o.reference)}${o.cp ? ` · CP ${escapeHtml(o.cp)}` : ''}</div>
          <div class="small gray">${escapeHtml(o.address||'—')}</div>
          ${comentario}
          ${notaHtml}
        </div>
      </div>`;
    }).join('');
    return `<div class="tech" style="border-left-color:${color}">
      <div class="tech-hdr" style="background:${color}1a">
        <span class="tname" style="color:${color}">${escapeHtml(rt.tecnico)}</span>
        <span class="tag ${rt.osrmUsed ? 'tag-exist' : 'tag-estimado'}">${rt.osrmUsed ? '✓ tiempos reales' : '~ tiempos estimados'}</span>
        ${kpiStripHtml([
          { val: rt.totalOrdenes, label: 'Paradas' },
          { val: rt.nuevasOrdenes, label: 'Nuevas' },
          { val: rt.existentesOrdenes, label: 'Ya asignadas' },
          { val: rt.horaFinEstimada, label: 'Fin estimado', color },
        ])}
        ${gaugeHtml(rt.totalOrdenes, 5, color)}
        ${qrPorTecnico[rt.tecnico]?.dataUrl ? `<a href="${qrPorTecnico[rt.tecnico].url}" class="qr-maps">
          <img src="${qrPorTecnico[rt.tecnico].dataUrl}" width="76" height="76" alt="QR ruta en Google Maps">
          <span>Ruta en<br>Google Maps</span>
        </a>` : ''}
      </div>
      ${timelineHtml(rt.orders)}
      <div class="stops">${cards || '<p class="empty">Sin órdenes</p>'}</div>
    </div>`;
  }).join('');
  const titleLabel = tecnicoFilter ? `Ruta ${tecnicoFilter} — ${dateLabel}` : `Rutas del día — ${dateLabel}`;
  return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>${titleLabel}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;color:#1f2937;padding:28px 36px;font-size:12px;background:#fff}
    .phdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #dc2626;padding-bottom:14px;margin-bottom:20px}
    .phdr h1{font-size:18px;font-weight:700;color:#dc2626}
    .phdr .date{font-size:14px;font-weight:600;color:#111827;text-transform:capitalize}
    .phdr .gen{font-size:10px;color:#9ca3af;text-align:right}
    .legend{display:flex;gap:16px;margin-bottom:18px;font-size:10px;color:#6b7280;flex-wrap:wrap}
    .legend span{display:flex;align-items:center;gap:4px}
    .l-p1{width:10px;height:10px;background:#fef2f2;border-left:3px solid #f87171;display:inline-block}
    .l-p2{width:10px;height:10px;background:#f0fdf4;border-left:3px solid #4ade80;display:inline-block}
    .l-ex{width:10px;height:10px;background:#eff6ff;border-left:3px solid #93c5fd;display:inline-block}

    .tech{margin-bottom:28px;page-break-inside:avoid;border-left:5px solid;border-radius:0 10px 10px 0;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.08)}
    .tech-hdr{display:flex;align-items:center;gap:18px;padding:12px 16px;flex-wrap:wrap}
    .tname{font-size:18px;font-weight:800}
    .qr-maps{display:flex;flex-direction:column;align-items:center;gap:2px;text-decoration:none;color:#374151;font-size:8px;text-align:center;line-height:1.2}
    .qr-maps img{display:block;border:1px solid #e5e7eb;border-radius:4px}

    .kpi-strip{display:flex;gap:16px;margin-left:auto}
    .kpi-tile{text-align:center;min-width:52px}
    .kpi-val{font-size:16px;font-weight:800;color:#111827;line-height:1.1}
    .kpi-label{font-size:8px;color:#6b7280;text-transform:uppercase;letter-spacing:.03em}

    .gauge{display:flex;align-items:center;gap:6px}
    .gauge-segs{display:flex;gap:2px}
    .gauge-seg{width:10px;height:14px;border-radius:2px;background:#e5e7eb}
    .gauge-label{font-size:9px;color:#6b7280;white-space:nowrap}

    .timeline{padding:10px 16px 4px;background:#fafafa;border-bottom:1px solid #f0f0f0}
    .tl-track{position:relative;height:22px;background:#eef0f3;border-radius:5px}
    .tl-block{position:absolute;top:1px;bottom:1px;border-radius:3px;font-size:8px;color:#fff;display:flex;align-items:center;justify-content:center;overflow:hidden;font-weight:700}
    .tl-p1{background:#f87171}.tl-p2{background:#4ade80}.tl-exist{background:#60a5fa}.tl-comida{background:#d1d5db;color:#4b5563}
    .tl-axis{display:flex;justify-content:space-between;font-size:8px;color:#9ca3af;margin-top:3px}

    .stops{padding:10px 16px 14px;display:flex;flex-direction:column;gap:8px}
    .stop{display:flex;gap:10px;border-radius:8px;padding:9px 10px}
    .stop.p1{background:#fef9f9}.stop.p2{background:#f6fdf8}.stop.exist{background:#f5f9ff}
    .stop-num{flex-shrink:0;width:22px;height:22px;border-radius:50%;color:#fff;font-size:11px;font-weight:800;display:flex;align-items:center;justify-content:center}
    .stop-body{flex:1;min-width:0}
    .stop-top{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:2px}
    .stop-time{font-size:11px;color:#111827;font-weight:700}
    .chip{font-size:8px;font-weight:700;padding:1px 6px;border-radius:999px}
    .chip-gray{background:#f3f4f6;color:#6b7280}.chip-alert{background:#fee2e2;color:#991b1b}

    .mono{font-family:monospace;white-space:nowrap}
    .cname{font-weight:700;font-size:12px;color:#111827}
    .ref{font-size:10px;color:#9ca3af;margin-top:1px}
    .comment{font-size:10px;color:#374151;font-style:italic;margin-top:3px;line-height:1.3}
    .ia-notas{margin-top:5px;padding:6px 8px;background:#f5f3ff;border-left:2px solid #8b5cf6;border-radius:0 5px 5px 0;font-size:10px;line-height:1.55}
    .ia-causa{color:#374151}
    .ia-diag{margin-top:2px}.ia-diag:first-child{margin-top:0}
    .ia-riesgos{margin-top:2px}.riesgo{display:inline-block;background:#fef3c7;color:#92400e;border-radius:3px;padding:1px 5px;margin-right:4px;font-size:9px}
    .ia-material{margin-top:2px;color:#374151}
    .material{display:inline-block;border-radius:3px;padding:1px 5px;margin-right:4px;font-size:9px}
    .mat-ok{background:#dcfce7;color:#166534}.mat-warn{background:#fee2e2;color:#991b1b}
    .mat-secundario{opacity:0.6}
    .ia-oportunidad{margin-top:2px;color:#5b21b6}
    .small{font-size:11px}.gray{color:#9ca3af}
    .tag{font-size:9px;padding:1px 5px;border-radius:3px;background:#f3f4f6;color:#374151}
    .tag-exist{background:#dbeafe;color:#1d4ed8}
    .tag-estimado{background:#fef3c7;color:#92400e}
    .empty{text-align:center;color:#9ca3af;padding:16px;font-size:11px}
    .footer{margin-top:28px;padding-top:10px;border-top:1px solid #e5e7eb;text-align:center;font-size:10px;color:#9ca3af}
    @media print{body{padding:14px 18px}@page{margin:1cm}.tech{page-break-inside:avoid;box-shadow:none}}
  </style></head><body>
  <div class="phdr">
    <div><h1>${tecnicoFilter?`Ruta · ${tecnicoFilter}`:'Rutas del día'}</h1><div class="date">${dateLabel}</div></div>
    <div class="gen">Generado ${new Date().toLocaleString('es-ES')}<br><strong style="color:#dc2626">Uso interno · Climatización Zapata</strong></div>
  </div>
  <div class="legend">
    <span><i class="l-p1"></i> Prioridad alta (pendiente / ausente)</span>
    <span><i class="l-p2"></i> Prioridad normal (gestionada)</span>
    <span><i class="l-ex"></i> Ya asignada</span>
  </div>
  ${Object.keys(routes).length===0
    ?'<p style="text-align:center;color:#9ca3af;padding:40px">Sin órdenes para esta fecha</p>'
    :techSections}
  <div class="footer">Horarios estimados (09:45–17:30 · comida flexible 13:30–15:00) — sujetos a cambios según incidencias del día${Object.values(notasPorOrden).length ? '<br>🔧/💡 Notas generadas por IA — material "orientativo" (no reservado), no sustituyen el criterio del técnico' : ''}</div>
  </body></html>`;
}

// Gráfico de barras horizontal (carga por técnico) — color por identidad de
// técnico (mismo criterio que TECH_MAP_COLORS en pantalla), ancho
// proporcional al máximo del día, nunca al revés (una ruta con 0 paradas no
// desaparece, sale con barra vacía).
export function barChartHtml(routeOrders) {
  const entries = Object.values(routeOrders);
  if (!entries.length) return '<p class="empty-inline">Sin rutas</p>';
  const max = Math.max(1, ...entries.map(rt => rt.totalOrdenes));
  return `<div class="barchart">${entries.map(rt => {
    const color = TECH_MAP_COLORS[rt.tecnico] || '#6b7280';
    const widthPct = (rt.totalOrdenes / max) * 100;
    return `<div class="bar-row">
      <span class="bar-label">${escapeHtml(rt.tecnico)}</span>
      <div class="bar-track"><div class="bar-fill" style="width:${widthPct}%;background:${color}"></div></div>
      <span class="bar-val">${rt.totalOrdenes}</span>
      <span class="bar-sub">${rt.nuevasOrdenes} nuevas · fin ${rt.horaFinEstimada}</span>
    </div>`;
  }).join('')}</div>`;
}

// Donut (conic-gradient CSS, sin librerías) — proporción nuevas/ya asignadas
// del día completo, de un vistazo.
export function donutHtml(nuevas, existentes) {
  const total = nuevas + existentes;
  if (!total) return '';
  const pctNuevas = Math.round((nuevas / total) * 100);
  return `<div class="donut-wrap">
    <div class="donut" style="background:conic-gradient(#f87171 0 ${pctNuevas}%, #93c5fd ${pctNuevas}% 100%)">
      <div class="donut-hole"><span class="donut-total">${total}</span><span class="donut-sub">paradas</span></div>
    </div>
    <div class="donut-legend">
      <span><i style="background:#f87171"></i> ${nuevas} nuevas (${pctNuevas}%)</span>
      <span><i style="background:#93c5fd"></i> ${existentes} ya asignadas (${100 - pctNuevas}%)</span>
    </div>
  </div>`;
}

// Hoja resumen de operaciones — Parte 1 del plan de notas IA (2026-10-05):
// carga por técnico, qué quedó fuera de la propuesta y por qué, avisos de
// material sin stock/fuera de catálogo (agregados de las notas IA ya
// generadas) y garantías de equipos a punto de caducar (<=30 días o ya
// caducadas). Siempre sobre TODAS las rutas, no se filtra por técnico.
export function buildOpsSummaryHTML(result, routeOrders, notasPorOrden, activosPorOrden) {
  const { fecha } = result;
  const dateLabel = new Date(fecha + 'T12:00:00').toLocaleDateString('es-ES', {
    weekday:'long', day:'2-digit', month:'long', year:'numeric',
  });

  const ordenesById = {};
  Object.values(routeOrders).forEach(rt => rt.orders.forEach(o => { ordenesById[o.id] = o; }));

  const rutas = Object.values(routeOrders);
  const totalParadas = rutas.reduce((s, rt) => s + rt.totalOrdenes, 0);
  const totalNuevas = rutas.reduce((s, rt) => s + rt.nuevasOrdenes, 0);
  const totalExistentes = rutas.reduce((s, rt) => s + rt.existentesOrdenes, 0);
  const totalAlertas = (result.sinZona?.length||0) + (result.sinDisponibilidad?.length||0) + (result.excluidasPorVentana?.length||0);

  const listaOFallback = (items, render) => items.length ? `<ul>${items.map(render).join('')}</ul>` : '<p class="empty-inline">Ninguna</p>';

  const fueraSinZona = listaOFallback(result.sinZona || [], o =>
    `<li>${escapeHtml(o.reference)} — ${escapeHtml(o.customerName)} <span class="gray">(${escapeHtml(o.motivo || `sin zona reconocida, CP ${o.cp || '—'}`)})</span></li>`);
  const fueraSinHueco = listaOFallback(result.sinDisponibilidad || [], o =>
    `<li>${escapeHtml(o.reference)} — ${escapeHtml(o.customerName)} <span class="gray">(${escapeHtml(o.motivo || 'sin huecos ese día')})</span></li>`);
  const fueraVentana = listaOFallback(result.excluidasPorVentana || [], o =>
    `<li>${escapeHtml(o.reference)} — ${escapeHtml(o.customerName)} <span class="gray">(${escapeHtml(o.tecnico)}: ${escapeHtml(o.motivo)})</span></li>`);

  // Material sin stock/fuera de catálogo, agregado de las notas ya generadas
  const materialPorNombre = {};
  Object.values(notasPorOrden).forEach(nota => {
    (nota.materialSugerido || []).forEach(m => {
      if (m.disponible) return;
      materialPorNombre[m.nombre] = (materialPorNombre[m.nombre] || 0) + 1;
    });
  });
  const avisosMaterial = Object.entries(materialPorNombre)
    .sort((a, b) => b[1] - a[1])
    .map(([nombre, n]) => `<li>${escapeHtml(nombre)} — pedido en ${n} orden${n > 1 ? 'es' : ''}, sin stock o fuera de catálogo</li>`)
    .join('') || '<p class="empty-inline">Ninguno</p>';

  // Garantías a punto de caducar o ya caducadas
  const hoy = Date.now();
  const garantias = [];
  Object.entries(activosPorOrden).forEach(([ordenId, activos]) => {
    const orden = ordenesById[ordenId] || ordenesById[Number(ordenId)];
    (activos || []).forEach(a => {
      if (!a.fechaGarantia) return;
      const dias = Math.round((new Date(a.fechaGarantia).getTime() - hoy) / 86400000);
      if (dias <= 30) garantias.push({ orden, equipo: [a.marca, a.modelo].filter(Boolean).join(' ') || 'Equipo', dias });
    });
  });
  garantias.sort((a, b) => a.dias - b.dias);
  const garantiasHtml = garantias.length
    ? `<ul>${garantias.map(g => `<li>${escapeHtml(g.equipo)}${g.orden ? ` — ${escapeHtml(g.orden.customerName)} (${escapeHtml(g.orden.reference)})` : ''} <span class="${g.dias < 0 ? 'alert' : 'gray'}">(${g.dias < 0 ? `caducada hace ${Math.abs(g.dias)}d` : `caduca en ${g.dias}d`})</span></li>`).join('')}</ul>`
    : '<p class="empty-inline">Ninguna</p>';

  return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Resumen operativo — ${dateLabel}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box}
    body{font-family:Arial,sans-serif;color:#1f2937;padding:28px 36px;font-size:12px;background:#fff}
    .phdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #dc2626;padding-bottom:14px;margin-bottom:20px}
    .phdr h1{font-size:18px;font-weight:700;color:#dc2626}
    .phdr .date{font-size:14px;font-weight:600;color:#111827;text-transform:capitalize}
    .phdr .gen{font-size:10px;color:#9ca3af;text-align:right}

    .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:22px}
    .kpi-card{background:#f9fafb;border-radius:10px;padding:12px 14px;text-align:center}
    .kpi-card .v{font-size:22px;font-weight:800;color:#111827;line-height:1.1}
    .kpi-card .l{font-size:9px;color:#6b7280;text-transform:uppercase;letter-spacing:.03em;margin-top:2px}
    .kpi-card.warn .v{color:#dc2626}

    section{margin-bottom:22px;page-break-inside:avoid}
    h2{font-size:13px;font-weight:700;color:#111827;background:#f9fafb;border-left:4px solid #dc2626;padding:7px 10px;border-radius:0 6px 6px 0;margin-bottom:10px}

    .charts-row{display:flex;gap:24px;align-items:flex-start;flex-wrap:wrap}
    .barchart{flex:1;min-width:260px;display:flex;flex-direction:column;gap:10px}
    .bar-row{display:grid;grid-template-columns:52px 1fr 24px;align-items:center;gap:8px;row-gap:1px}
    .bar-label{font-size:11px;font-weight:700;color:#111827}
    .bar-track{height:14px;background:#f1f3f5;border-radius:4px;overflow:hidden}
    .bar-fill{height:100%;border-radius:4px}
    .bar-val{font-size:11px;font-weight:700;color:#111827;text-align:right}
    .bar-sub{grid-column:2/4;font-size:9px;color:#9ca3af}

    .donut-wrap{display:flex;flex-direction:column;align-items:center;gap:8px}
    .donut{width:110px;height:110px;border-radius:50%;display:flex;align-items:center;justify-content:center}
    .donut-hole{width:70px;height:70px;border-radius:50%;background:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center}
    .donut-total{font-size:18px;font-weight:800;color:#111827;line-height:1}
    .donut-sub{font-size:8px;color:#9ca3af;text-transform:uppercase}
    .donut-legend{display:flex;flex-direction:column;gap:3px;font-size:10px;color:#374151}
    .donut-legend span{display:flex;align-items:center;gap:5px}
    .donut-legend i{width:8px;height:8px;border-radius:2px;display:inline-block}

    .center{text-align:center}.mono{font-family:monospace}
    ul{list-style:none;padding-left:4px}
    li{padding:3px 0;border-bottom:1px solid #f9fafb;font-size:11px}
    .gray{color:#9ca3af;font-size:10px}
    .alert{color:#dc2626;font-weight:600;font-size:10px}
    .empty-inline{color:#9ca3af;font-size:11px;padding:4px 0}
    .footer{margin-top:28px;padding-top:10px;border-top:1px solid #e5e7eb;text-align:center;font-size:10px;color:#9ca3af}
    @media print{body{padding:14px 18px}@page{margin:1cm}section{page-break-inside:avoid}}
  </style></head><body>
  <div class="phdr">
    <div><h1>Resumen operativo</h1><div class="date">${dateLabel}</div></div>
    <div class="gen">Generado ${new Date().toLocaleString('es-ES')}<br><strong style="color:#dc2626">Uso interno · Climatización Zapata</strong></div>
  </div>

  <div class="kpi-grid">
    <div class="kpi-card"><div class="v">${totalParadas}</div><div class="l">Paradas totales</div></div>
    <div class="kpi-card"><div class="v">${rutas.length}</div><div class="l">Técnicos activos</div></div>
    <div class="kpi-card${totalAlertas>0?' warn':''}"><div class="v">${totalAlertas}</div><div class="l">Quedaron fuera</div></div>
    <div class="kpi-card${Object.keys(materialPorNombre).length>0?' warn':''}"><div class="v">${Object.keys(materialPorNombre).length}</div><div class="l">Avisos de material</div></div>
  </div>

  <section>
    <h2>Carga por técnico</h2>
    <div class="charts-row">
      ${barChartHtml(routeOrders)}
      ${donutHtml(totalNuevas, totalExistentes)}
    </div>
  </section>

  <section>
    <h2>Quedaron fuera de la propuesta</h2>
    <p class="gray" style="margin-bottom:6px">Sin zona reconocida</p>${fueraSinZona}
    <p class="gray" style="margin:8px 0 6px">Sin huecos ese día</p>${fueraSinHueco}
    <p class="gray" style="margin:8px 0 6px">Fuera de la ventana horaria (no cabían antes de las 17:30)</p>${fueraVentana}
  </section>

  <section>
    <h2>Avisos de material (sin stock o fuera de catálogo)</h2>
    ${avisosMaterial}
  </section>

  <section>
    <h2>Garantías a punto de caducar (≤30 días) o ya caducadas</h2>
    ${garantiasHtml}
  </section>

  <div class="footer">Stock y garantías orientativos — StelOrder es la fuente de verdad final</div>
  </body></html>`;
}
